/**
 * Client-side auth — reads session from a lightweight GET /api/auth/session endpoint.
 *
 * This is intentionally thin: the heavy lifting (JWT verification, revocation
 * check) stays server-side. The browser only needs to know basic session state
 * for rendering (show/hide auth-gated UI, redirect logic in page components).
 *
 * Pattern: useAuth() hook backed by TanStack Query so the result is cached and
 * deduped across components. staleTime=0 so navigations always re-validate.
 */

import { useQuery } from '@tanstack/react-query'

export interface ClientSession {
  userId: string
  email: string | null
  role: string
  isAdmin: boolean
}

async function fetchSession(): Promise<ClientSession | null> {
  const res = await fetch('/api/auth/session', { credentials: 'include' })
  if (res.status === 401 || res.status === 204) return null
  if (!res.ok) return null
  return (await res.json()) as ClientSession
}

export function useAuth(): { session: ClientSession | null; loading: boolean } {
  const { data, isLoading } = useQuery<ClientSession | null>({
    queryKey: ['auth', 'session'],
    queryFn: fetchSession,
    staleTime: 0,
    retry: false,
  })
  return { session: data ?? null, loading: isLoading }
}
